import { BoundingBox, Coordinate, Coordinates, MapData } from '@bpd-library/types';
import turfDistance from '@turf/distance';

const DEGREE_PER_KM = 0.0089;

const calculateBoundingBox = (coordinates: Coordinates): [number, number, number, number] => {
    let minX: number | undefined,
        minY: number | undefined,
        maxX: number | undefined,
        maxY: number | undefined;

    coordinates.flat(1).forEach(([x, y]) => {
        minX = !minX || x < minX ? x : minX;
        minY = !minY || y < minY ? y : minY;
        maxX = !maxX || x > maxX ? x : maxX;
        maxY = !maxY || y > maxY ? y : maxY;
    });

    return [minX || 0, minY || 0, maxX || 0, maxY || 0];
};

const concatMapDataCoordinates = (mapData: MapData): Coordinates =>
    mapData.reduce<Coordinates>((coordinates, item) => coordinates.concat(item.coordinates), []);

const calculateMapDataBoundingBox = (mapData: MapData): BoundingBox => {
    const coordinates = concatMapDataCoordinates(mapData);

    return calculateBoundingBox(coordinates);
};

const calculateMapDataCenter = (mapData: MapData): Coordinate => {
    const coordinates = concatMapDataCoordinates(mapData);

    return calculateCenter(coordinates);
};

const calculateBoundingBoxCenter = ([minX, minY, maxX, maxY]: [number, number, number, number]): [
    number,
    number,
] => {
    return [(minX + maxX) / 2, (minY + maxY) / 2];
};

const calculateCenter = (coordinates: Coordinates): Coordinate => {
    return calculateBoundingBoxCenter(calculateBoundingBox(coordinates));
};

const mergeBoundingBoxes = (boundingBoxes: BoundingBox[]) => {
    const mergedBoundingBox = boundingBoxes.reduce<BoundingBox>((merged, boundingBox) => {
        merged[0] = merged[0] < boundingBox[0] ? merged[0] : boundingBox[0];
        merged[1] = merged[1] < boundingBox[1] ? merged[1] : boundingBox[1];
        merged[2] = merged[2] > boundingBox[2] ? merged[2] : boundingBox[2];
        merged[3] = merged[3] > boundingBox[3] ? merged[3] : boundingBox[3];

        return merged;
    }, boundingBoxes[0]);

    return mergedBoundingBox;
};

const expandBoundingBox = ([minX, minY, maxX, maxY]: BoundingBox, km: number): BoundingBox => {
    const degreePadding = DEGREE_PER_KM * km;

    minX -= degreePadding;
    minY -= degreePadding;
    maxX += degreePadding;
    maxY += degreePadding;

    return [minX, minY, maxX, maxY];
};

const withinBoundingBox = (
    coordinates: Coordinates,
    boundingBox: BoundingBox,
    kmPadding: number = 0,
): boolean => {
    if (kmPadding) {
        boundingBox = expandBoundingBox(boundingBox, kmPadding);
    }

    const flatCoordinates = coordinates.flat(1);
    const [minX, minY, maxX, maxY] = boundingBox;

    for (let i = 0; i < flatCoordinates.length; i++) {
        const [x, y] = flatCoordinates[i];

        if (x < maxX && x > minX && y < maxY && y > minY) return true;
    }

    return false;
};

const calculateDistance = (pointA: Coordinate, pointB: Coordinate): number => {
    const distanceX = (pointA[0] - pointB[0]) ** 2;
    const distanceY = (pointA[1] - pointB[1]) ** 2;

    return Math.sqrt(distanceX + distanceY);
};

const calculateDistanceInKm = (pointA: Coordinate, pointB: Coordinate): number => {
    return turfDistance(pointA, pointB);
};

export {
    calculateBoundingBox,
    calculateBoundingBoxCenter,
    calculateCenter,
    calculateDistance,
    calculateDistanceInKm,
    calculateMapDataBoundingBox,
    calculateMapDataCenter,
    withinBoundingBox,
    expandBoundingBox,
    mergeBoundingBoxes,
};
